import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Database, Workflow, Shield, Code, BarChart3 } from 'lucide-react';

const AITools = () => {
  const solutions = [
    {
      icon: <Bot size={32} />,
      title: 'Internal Copilots & Assistants',
      description: 'Custom AI assistants that understand your business context, answer employee questions, and automate routine tasks.'
    },
    {
      icon: <Workflow size={32} />,
      title: 'Workflow Automation Agents',
      description: 'Autonomous agents that orchestrate multi-step processes, from data gathering to decision-making and execution.'
    },
    {
      icon: <Database size={32} />,
      title: 'Data Extraction & Analysis',
      description: 'LLM-powered tools that extract insights from documents, emails, and unstructured data at scale.'
    },
    {
      icon: <Code size={32} />,
      title: 'API & System Integration',
      description: 'Agents that seamlessly connect with your existing tools, databases, and third-party services.'
    },
    {
      icon: <BarChart3 size={32} />,
      title: 'Decision Support Dashboards',
      description: 'AI-enhanced analytics platforms that provide real-time insights and scenario modeling for executives.'
    },
    {
      icon: <Shield size={32} />,
      title: 'Compliance & Governance Tools',
      description: 'Automated monitoring and reporting systems that ensure regulatory compliance and audit readiness.'
    }
  ];

  const useCases = [
    {
      industry: 'Manufacturing',
      examples: ['Supply chain optimization', 'Predictive maintenance', 'Quality control automation']
    },
    {
      industry: 'Energy',
      examples: ['Grid management', 'Demand forecasting', 'Asset performance optimization']
    },
    {
      industry: 'Government',
      examples: ['Policy impact modeling', 'Citizen service automation', 'Regulatory compliance monitoring']
    },
    {
      industry: 'Finance',
      examples: ['Risk assessment automation', 'Fraud detection', 'Regulatory reporting']
    }
  ];

  const process = [
    {
      phase: 'Discovery',
      duration: '1-2 weeks',
      activities: 'Workshops with stakeholders, technical assessment, use case prioritization'
    },
    {
      phase: 'Design & Prototyping',
      duration: '2-4 weeks',
      activities: 'Architecture design, proof-of-concept development, user testing'
    },
    {
      phase: 'Integration & Deployment',
      duration: '4-12 weeks',
      activities: 'System integration, security hardening, pilot rollout, training'
    },
    {
      phase: 'Optimization & Support',
      duration: 'Ongoing',
      activities: 'Performance monitoring, model tuning, feature enhancements, SLA support'
    }
  ];

  return (
    <div style={{ paddingTop: '80px', background: 'var(--bg-primary)' }}>
      {/* Hero */}
      <section style={{
        padding: '100px 7.6923%',
        background: 'var(--bg-primary)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h1 className="display-huge" style={{ marginBottom: '24px' }}>
            AI Tools & Agents
          </h1>
          <p className="body-large" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
            We design, build, and integrate bespoke AI tools and autonomous agents that streamline operations, enhance decision-making, and unlock new capabilities for your organization.
          </p>
          <Link to="/contact" className="btn-primary">
            Schedule an AI architecture session
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* What We Build */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            What We Build
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '40px'
          }}>
            {solutions.map((solution, idx) => (
              <div
                key={idx}
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-subtle)',
                  padding: '32px',
                  transition: 'all 0.4s ease'
                }}
                className="dark-hover"
              >
                <div style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>
                  {solution.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {solution.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {solution.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases by Industry */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Typical Use Cases
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '40px'
          }}>
            {useCases.map((useCase, idx) => (
              <div
                key={idx}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  padding: '32px'
                }}
              >
                <h3 className="heading-2" style={{ marginBottom: '20px', color: 'var(--brand-primary)' }}>
                  {useCase.industry}
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {useCase.examples.map((example, i) => (
                    <li key={i} style={{
                      fontSize: '16px',
                      color: 'var(--text-secondary)',
                      marginBottom: '12px',
                      paddingLeft: '24px',
                      position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        color: 'var(--brand-primary)'
                      }}>→</span>
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Our Process
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '40px'
          }}>
            {process.map((step, idx) => (
              <div key={idx} style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-subtle)',
                padding: '32px'
              }}>
                <div style={{
                  fontSize: '14px',
                  color: 'var(--brand-primary)',
                  fontWeight: 500,
                  marginBottom: '8px'
                }}>
                  {step.duration}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                  {step.phase}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {step.activities}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="display-medium" style={{ marginBottom: '24px' }}>
            Technology Stack
          </h2>
          <p className="body-large" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
            We're technology-agnostic and choose the best tools for each use case. Our approach prioritizes reliability, security, and maintainability over hype.
          </p>
          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            padding: '40px',
            textAlign: 'left'
          }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, columns: '2', columnGap: '40px' }}>
              {[
                'Large Language Models (GPT-5, Claude, Gemini)',
                'Vector databases & embeddings',
                'FastAPI, Python, Node.js',
                'Cloud platforms (AWS, Azure, GCP)',
                'Kubernetes & containerization',
                'PostgreSQL, MongoDB',
                'API orchestration & webhooks',
                'Security & compliance frameworks'
              ].map((tech, i) => (
                <li key={i} style={{
                  fontSize: '16px',
                  color: 'var(--text-secondary)',
                  marginBottom: '12px',
                  paddingLeft: '24px',
                  position: 'relative',
                  breakInside: 'avoid'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    color: 'var(--brand-primary)'
                  }}>✓</span>
                  {tech}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-secondary)', textAlign: 'center' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="display-large" style={{ marginBottom: '24px' }}>
            Let's build something powerful
          </h2>
          <p className="body-large" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
            Book a session to discuss your AI needs and explore what's possible.
          </p>
          <Link to="/contact" className="btn-primary">
            Get started
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AITools;
